import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import UploadAdCreativeCampaignForm from '../../../../Multiplatform/UploadAdCreative/UploadAdCreativeCampaignForm'
import NewCampaignImpressionsFields from './FormFields/NewCampaignImpressionsFields'
import NewCampaignBudgetFields from './FormFields/NewCampaignBudgetFields'

import useRightBudgetAndImpressionsCalculation from '../../../../Multiplatform/UploadAdCreative/useRightBudgetAndImpressionsCalculation'

import { formatOptionsList } from '../../../../../features/formatters'
import { showToasts } from '../../../../../helpers/toasts'
import { checkIfCampaignHasLineItem } from '../../../CampaignForms/helpers'

import { getLineItems } from '../../../../../modules/actions/lineItems'
import { setRightUploadAdData } from '../../../../../modules/actions/rights'
import { createCampaign } from '../../../../../modules/actions/campaigns'
import {
  campaignCreateErrorSelector,
  campaignCreateIsLoadingSelector
} from '../../../../../modules/selectors/campaigns'
import { rightSelector } from '../../../../../modules/selectors/rights'
import { lineItemsLoadingSelector } from '../../../../../modules/selectors/lineItems'
import { combinedCampaignsSelector } from '../../../../../modules/selectors/combinedData'
import { selectedGoogleAdAccountSelector } from '../../../../../modules/selectors/adAccounts'
import { selectedPartnershipSelector } from '../../../../../modules/selectors/partnerships'

import { CAMPAIGN } from '../../../../Multiplatform/UploadAdCreative/UploadAdCreativeCampaignForm/fields'
import { createCampaignPayload, getInitialValues, getValidationSchema, IMPRESSIONS } from './fields'
import { DIGITAL_ALLOCATION_TYPE_IMPRESSIONS } from '../../../../Multiplatform/Partnerships/RightForms/fields'

import { TOAST_TYPE } from '../../../../../constants/other'
import { GOOGLE_PLATFORM, GOOGLE_PROVIDER_PLATFORM } from '../../../../../constants/selectLists/platformList'
import { LIFETIME_DATE_PRESET } from '../../../../../constants/selectLists/listItemFiltersList'
import { LIST_ITEM_OPTION, OPTION_EXISTING_LIST_ITEM } from '../../../../../hooks/formHooks/listItemFormHooks/fields'
import { END_DATE, START_DATE } from '../../../../ReusableFormFields/StartEndDateRangeFields/fields'
import { BUDGET_DAILY } from '../../../../Multiplatform/CampaignForms/fields'

import useDrawerFormStyles from '../../../../../styles/common/drawerForms'

const campaignStepValidationFields = [START_DATE, END_DATE, IMPRESSIONS, BUDGET_DAILY]

const CampaignForm = ({ formProgressItems, onSuccessSubmit, onSelectExistingCampaign }) => {
  const drawerFormClasses = useDrawerFormStyles()

  const dispatch = useDispatch()

  const [manualSuccessSubmit, setManualSuccessSubmit] = useState(false)

  const right = useSelector(rightSelector)

  const { allocation_type: allocationTypeOnDigitalRight } = right

  const showImpressions = allocationTypeOnDigitalRight === DIGITAL_ALLOCATION_TYPE_IMPRESSIONS

  const combinedCampaigns = useSelector(combinedCampaignsSelector)
  const { id: googleAdAccountId } = useSelector(selectedGoogleAdAccountSelector)
  const campaignCreateError = useSelector(campaignCreateErrorSelector)
  const selectedPartnership = useSelector(selectedPartnershipSelector)

  const { campaigns = [] } = combinedCampaigns

  const { remainingBudget, remainingImpressions } = useRightBudgetAndImpressionsCalculation()

  const googleCampaigns = useMemo(
    () => campaigns.filter(campaign => campaign.provider === GOOGLE_PROVIDER_PLATFORM && campaign.status !== 'deleted'),
    [campaigns]
  )

  const isCampaignsExist = !!googleCampaigns.length

  const formattedCampaigns = formatOptionsList({
    list: googleCampaigns,
    valueName: 'id',
    labelName: 'name'
  })

  const onSubmit = useCallback(
    values => {
      // if existing campaign is selected, find full data and set to redux state
      if (values[LIST_ITEM_OPTION] === OPTION_EXISTING_LIST_ITEM) {
        const selectedCampaignFullData = formattedCampaigns.find(campaign => values[CAMPAIGN] === campaign.id)

        const campaignHasLineItem = checkIfCampaignHasLineItem(selectedCampaignFullData.advertising_channel_type)

        dispatch(setRightUploadAdData({ campaign: selectedCampaignFullData }, GOOGLE_PLATFORM))

        if (campaignHasLineItem) {
          const campaignAdAccount = selectedCampaignFullData?.['ad_account_id'] || selectedCampaignFullData?.['account']

          dispatch(
            getLineItems(
              {
                account: campaignAdAccount,
                campaign_id: values[CAMPAIGN],
                date_preset: LIFETIME_DATE_PRESET
              },
              GOOGLE_PLATFORM
            )
          )
        } else {
          // imitate success submit when selecting existing campaign, which doesn't have line items
          setManualSuccessSubmit(true)
        }
      } else {
        const campaignData = createCampaignPayload({
          values,
          adAccountId: googleAdAccountId,
          right,
          selectedPartnership
        })

        dispatch(
          createCampaign({ campaignData }, GOOGLE_PLATFORM, {
            pushToCombinedCampaignsList: true
          })
        )
      }
    },
    [dispatch, formattedCampaigns, googleAdAccountId, right, selectedPartnership]
  )

  const initialValues = useMemo(
    () => getInitialValues(isCampaignsExist, allocationTypeOnDigitalRight),
    [isCampaignsExist, allocationTypeOnDigitalRight]
  )
  const formik = useFormik({
    initialValues,
    validationSchema: getValidationSchema({
      showImpressions,
      remainingImpressions,
      remainingBudget
    }),
    onSubmit
  })

  const { values } = formik

  const { [LIST_ITEM_OPTION]: campaignOption } = values

  const handleSuccessSubmit = useCallback(() => {
    if (campaignOption === OPTION_EXISTING_LIST_ITEM) {
      onSelectExistingCampaign()
    } else {
      onSuccessSubmit()
    }
  }, [campaignOption, onSelectExistingCampaign, onSuccessSubmit])

  useEffect(() => {
    if (campaignCreateError?.errors?.amount_micros) {
      showToasts({
        type: TOAST_TYPE.error,
        message: campaignCreateError.errors.amount_micros
      })
    }
  }, [campaignCreateError])

  // when we use existing campaign, we need to pre-load line items for the next LineItem form
  // in this case submit form loads line item. However, we don't do the same for error selectors,
  // because we don't block user for accessing next form when line items get request failed
  const isLoadingSelector =
    campaignOption === OPTION_EXISTING_LIST_ITEM ? lineItemsLoadingSelector : campaignCreateIsLoadingSelector

  return (
    <div className={drawerFormClasses.formContainer}>
      <UploadAdCreativeCampaignForm
        formik={formik}
        onSuccessSubmit={handleSuccessSubmit}
        platform={GOOGLE_PLATFORM}
        manualSuccessSubmit={manualSuccessSubmit}
        adAccountId={googleAdAccountId}
        existedCampaigns={formattedCampaigns}
        isLoadingSelector={isLoadingSelector}
        campaignStepValidationFields={campaignStepValidationFields}
      >
        {showImpressions ? (
          <NewCampaignImpressionsFields formik={formik} remainingImpressions={remainingImpressions} />
        ) : (
          <NewCampaignBudgetFields formik={formik} remainingBudget={remainingBudget} />
        )}
      </UploadAdCreativeCampaignForm>
    </div>
  )
}

export default CampaignForm
